import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Send, CheckCircle2, MessageSquareQuote, Search, Loader2, Heart, Lock, Edit2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { countryName, flagEmoji } from "@/data/countries";
import {
  getCommentSlice, TOTAL_COMMENTS,
  COUNTRIES_IN_DATASET, SERVICES_FOR_FILTER,
} from "@/data/commentsGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;
const LIKED_KEY = "ca_liked_comments_v1";

const readLikedSet = () => {
  try {
    const raw = localStorage.getItem(LIKED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};
const writeLikedSet = (set) => {
  try {
    localStorage.setItem(LIKED_KEY, JSON.stringify([...set]));
  } catch { /* ignore storage errors */ }
};

export const CommentsSection = () => {
  const SHOW_PUBLIC_FORM = false;
  const { lang } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const authHeader = () => {
    const t = localStorage.getItem("ca_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [query, setQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  // Real likes state
  const [extraLikes, setExtraLikes] = useState({}); // {id: serverExtraCount}
  const [likedSet, setLikedSet] = useState(() => readLikedSet());

  // Fetch real likes once on mount
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/comments/likes`)
      .then((r) => alive && setExtraLikes(r.data || {}))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleLike = useCallback(async (commentId) => {
    if (!user) {
      toast.info(lang === "es" ? "Regístrate para dar me gusta" : "Sign up to like");
      return;
    }
    if (likedSet.has(commentId)) return; // already liked
    // Optimistic update
    const next = new Set(likedSet);
    next.add(commentId);
    setLikedSet(next);
    writeLikedSet(next);
    setExtraLikes((cur) => ({ ...cur, [commentId]: (cur[commentId] || 0) + 1 }));
    try {
      const res = await axios.post(`${API}/comments/${commentId}/like`);
      // Sync with server total if different
      setExtraLikes((cur) => ({ ...cur, [commentId]: res.data.extra_likes }));
    } catch {
      // Rollback on error
      const rollback = new Set(likedSet);
      rollback.delete(commentId);
      setLikedSet(rollback);
      writeLikedSet(rollback);
      setExtraLikes((cur) => ({ ...cur, [commentId]: Math.max(0, (cur[commentId] || 1) - 1) }));
      toast.error(lang === "es" ? "Error al dar like" : "Error liking");
    }
  }, [likedSet, lang, user]);

  // Real user testimonials (approved by admin), prepended to the generated list
  const [realComments, setRealComments] = useState([]);
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/user-comments?limit=200`, { headers: authHeader?.() || {} })
      .then((r) => alive && setRealComments(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Censor real client names using the same rule as the generated ones:
  // first name stays visible, remaining name parts are partially starred.
  // Matches the visual language of the generator's `censorName` helper.
  const _censorRealName = (fullName) => {
    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "Cliente";
    const [first, ...rest] = parts;
    const star = (w, keep = 1) => (w.length <= keep ? w : w.slice(0, keep) + "*".repeat(Math.max(3, w.length - keep)));
    return [first, ...rest.map((w) => star(w, 1))].join(" ");
  };

  const realAsCards = useMemo(
    () => (realComments || []).map((rc) => ({
      id: `u_${rc.id}`,
      realId: rc.id,
      name: _censorRealName(rc.name),
      fullName: rc.name,
      initials: (rc.name || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase(),
      country: { name: countryName(rc.country), flag: flagEmoji(rc.country) },
      countryCode: rc.country || "",
      time: rc.approved_at ? new Date(rc.approved_at).toLocaleDateString() : (rc.created_at ? new Date(rc.created_at).toLocaleDateString() : ""),
      text: rc.text,
      likes: rc.likes || 0,
      verified: true,
      avatarColor: "bg-emerald-500/30 text-emerald-100 border-emerald-400/40",
      rating: rc.rating || 5,
      lang: rc.lang || "es",
      services: rc.service ? [rc.service] : [],
      serviceLabels: [],
      serviceCategories: [],
      isReal: true,
    })),
    [realComments],
  );

  // ---- Admin-only mutations on REAL testimonials -------------------
  const adminBumpLikes = useCallback(async (realId, delta) => {
    try {
      const r = await axios.post(
        `${API}/admin/user-comments/${realId}/likes`,
        { delta },
        { headers: authHeader() },
      );
      // Update local likes count immediately so the admin sees the effect
      setRealComments((prev) => prev.map((rc) => rc.id === realId ? { ...rc, likes: r.data.likes } : rc));
      toast.success(`+${delta} me gusta`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  }, []);

  const adminDeleteReal = useCallback(async (realId) => {
    if (!window.confirm("¿Eliminar este testimonio del sitio? Es permanente.")) return;
    try {
      await axios.delete(`${API}/admin/user-comments/${realId}`, { headers: authHeader() });
      setRealComments((prev) => prev.filter((rc) => rc.id !== realId));
      toast.success("Testimonio eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  }, []);

  const adminEditReal = useCallback(async (realId, fields) => {
    try {
      await axios.patch(`${API}/admin/user-comments/${realId}`, fields, { headers: authHeader() });
      setRealComments((prev) => prev.map((rc) => rc.id === realId ? { ...rc, ...fields } : rc));
      toast.success("Testimonio actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo editar");
    }
  }, []);

  const hasActiveFilter = countryFilter !== "all" || serviceFilter !== "all" || !!query.trim();

  // Generated slice up to visible, prepended with real approved user comments.
  // When any filter is active we generate ALL comments so filtering can
  // reach the entire dataset (not just the currently paginated view).
  const comments = useMemo(() => {
    const count = hasActiveFilter ? TOTAL_COMMENTS : visible;
    const generated = getCommentSlice(0, count);
    return [...realAsCards, ...generated];
  }, [visible, realAsCards, hasActiveFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return comments.filter((c) => {
      if (countryFilter !== "all" && c.countryCode !== countryFilter) return false;
      if (serviceFilter !== "all") {
        const svc = (c.services || []);
        if (!svc.includes(serviceFilter)) return false;
      }
      if (q) {
        const inText = (c.text || "").toLowerCase().includes(q);
        const inName = (c.fullName || "").toLowerCase().includes(q);
        const inCountry = (c.country?.name || "").toLowerCase().includes(q);
        if (!inText && !inName && !inCountry) return false;
      }
      return true;
    });
  }, [comments, query, countryFilter, serviceFilter]);

  // When filters are active, cap the visible list to `visible` items of
  // the filtered result. When they are not, the slice already respects
  // `visible` at generation time.
  const shownFiltered = useMemo(
    () => (hasActiveFilter ? filtered.slice(0, visible) : filtered),
    [filtered, hasActiveFilter, visible],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error(lang === "es" ? "Completa todos los campos" : "Fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/user-comments`, {
        name: name.trim(),
        country: user?.country_code || (lang === "en" ? "US" : "MX"),
        service: "lectura_completa",
        text: text.trim(),
        rating,
        lang,
      });
      toast.success(
        lang === "es"
          ? "¡Gracias! Tu testimonio será revisado por el administrador antes de publicarse."
          : "Thanks! Your testimonial will be reviewed by the admin before publishing."
      );
      setName("");
      setText("");
      setRating(5);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" && detail?.message
        ? detail.message
        : (typeof detail === "string" ? detail : (lang === "es" ? "No se pudo enviar el testimonio." : "Could not submit."));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = {
    eyebrow: lang === "es" ? "Testimonios Reales" : "Real Testimonials",
    title: lang === "es" ? "Lo que dicen mis clientes" : "What my clients say",
    subtitle:
      lang === "es"
        ? `Más de ${TOTAL_COMMENTS.toLocaleString()} personas en toda Latinoamérica confiaron y obtuvieron resultados.`
        : `Over ${TOTAL_COMMENTS.toLocaleString()} people across Latin America trusted and got results.`,
    statRating: lang === "es" ? "Rating promedio" : "Average rating",
    statClients: lang === "es" ? "Clientes felices" : "Happy clients",
    statCountries: lang === "es" ? "Países" : "Countries",
    formTitle: lang === "es" ? "Deja tu testimonio" : "Leave your testimonial",
    formSubtitle:
      lang === "es"
        ? "Comparte tu experiencia. Será revisado antes de publicarse."
        : "Share your experience. It will be reviewed before publishing.",
    formName: lang === "es" ? "Tu nombre" : "Your name",
    formText: lang === "es" ? "Tu experiencia..." : "Your experience...",
    formRating: lang === "es" ? "Calificación" : "Rating",
    formSubmit: lang === "es" ? "Enviar Comentario" : "Send Comment",
    submitting: lang === "es" ? "Enviando..." : "Sending...",
    searchPlaceholder:
      lang === "es" ? "Buscar por nombre, país o palabra..." : "Search by name, country or word...",
    loadMore: lang === "es" ? "Ver más testimonios" : "Load more testimonials",
    showing: lang === "es" ? "Mostrando" : "Showing",
    of: lang === "es" ? "de" : "of",
    noResults: lang === "es" ? "Sin resultados" : "No results",
    filterCountry: lang === "es" ? "Filtrar por país" : "Filter by country",
    filterService: lang === "es" ? "Filtrar por servicio" : "Filter by service",
    allCountries: lang === "es" ? "Todos los países" : "All countries",
    allServices: lang === "es" ? "Todos los servicios" : "All services",
    clearFilters: lang === "es" ? "Limpiar filtros" : "Clear filters",
  };

  return (
    <section
      id="testimonios"
      className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden"
      data-testid="comments-section"
    >
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#d4af37]/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <MessageSquareQuote className="w-4 h-4" />
            <span className="uppercase tracking-[0.3em] text-xs">{copy.eyebrow}</span>
            <MessageSquareQuote className="w-4 h-4" />
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-3">
            {copy.title}
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto italic mb-8">
            {copy.subtitle}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 max-w-2xl mx-auto gap-4">
            <div className="glass-dark rounded-xl px-3 py-4">
              <div className="flex items-center justify-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="w-3.5 h-3.5 text-[#d4af37] fill-[#d4af37]" />
                ))}
              </div>
              <div className="font-serif text-2xl gold-text">4.9</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                {copy.statRating}
              </div>
            </div>
            <div className="glass-dark rounded-xl px-3 py-4">
              <div className="font-serif text-3xl gold-text">{TOTAL_COMMENTS.toLocaleString()}+</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                {copy.statClients}
              </div>
            </div>
            <div className="glass-dark rounded-xl px-3 py-4">
              <div className="font-serif text-3xl gold-text">20</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                {copy.statCountries}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Registration invite for guests */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 glass-dark border border-[#d4af37]/30 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4 max-w-3xl mx-auto"
            data-testid="register-invite"
          >
            <Lock className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
            <p className="text-sm text-neutral-300 flex-1 text-center sm:text-left">
              {lang === "es"
                ? "Regístrate gratis para dar me gusta a testimonios, hablar conmigo por chat y agendar citas. Tu privacidad es 100% respetada."
                : "Sign up free to like testimonials, chat with me, and book appointments. 100% private."}
            </p>
            <Link to="/registro" data-testid="invite-register-btn" className="btn-gold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest whitespace-nowrap">
              {lang === "es" ? "Crear Cuenta" : "Sign Up"}
            </Link>
          </motion.div>
        )}

        {/* Comment form — desactivado: los testimonios sólo se publican mediante el enlace que envía el administrador */}
        {SHOW_PUBLIC_FORM && (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          data-testid="comment-form"
          className="glass-dark border border-[#d4af37]/40 rounded-2xl p-6 lg:p-8 mb-12 max-w-3xl mx-auto"
        >
          <h3 className="font-serif text-2xl text-white mb-1">{copy.formTitle}</h3>
          <p className="text-xs text-neutral-400 mb-5">{copy.formSubtitle}</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <Input
              data-testid="comment-name"
              required
              placeholder={copy.formName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="sm:col-span-2 bg-black/40 border-white/10 text-white"
            />
            <div className="flex items-center justify-center gap-1 rounded-md border border-white/10 bg-black/40 px-3" data-testid="comment-rating">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setRating(s)}
                  className="p-1 group"
                  aria-label={`${s} stars`}
                >
                  <Star
                    className={`w-5 h-5 transition-all ${
                      s <= rating
                        ? "text-[#d4af37] fill-[#d4af37]"
                        : "text-neutral-600 group-hover:text-[#d4af37]/60"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <Textarea
            data-testid="comment-text"
            required
            placeholder={copy.formText}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="bg-black/40 border-white/10 text-white mb-4"
          />

          <button
            type="submit"
            data-testid="comment-submit"
            disabled={submitting}
            className="btn-gold w-full sm:w-auto px-8 py-3.5 rounded-full uppercase text-xs tracking-[0.2em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {copy.submitting}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> {copy.formSubmit}
              </>
            )}
          </button>
        </motion.form>
        )}

        {/* Search + Filters */}
        <div className="max-w-4xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-3">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input
              data-testid="comment-search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setVisible(PAGE_SIZE); }}
              placeholder={copy.searchPlaceholder}
              className="pl-10 bg-black/40 border-white/10 text-white"
            />
          </div>
          <Select
            value={countryFilter}
            onValueChange={(v) => { setCountryFilter(v); setVisible(PAGE_SIZE); }}
          >
            <SelectTrigger
              data-testid="filter-country"
              className="bg-black/40 border-white/10 text-white"
              aria-label={copy.filterCountry}
            >
              <SelectValue placeholder={copy.filterCountry} />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-white/10 text-white max-h-72">
              <SelectItem value="all" data-testid="filter-country-all">{copy.allCountries}</SelectItem>
              {COUNTRIES_IN_DATASET.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`filter-country-${c.code}`}>
                  <span className="mr-2">{c.flag}</span>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={serviceFilter}
            onValueChange={(v) => { setServiceFilter(v); setVisible(PAGE_SIZE); }}
          >
            <SelectTrigger
              data-testid="filter-service"
              className="bg-black/40 border-white/10 text-white"
              aria-label={copy.filterService}
            >
              <SelectValue placeholder={copy.filterService} />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-white/10 text-white max-h-72">
              <SelectItem value="all" data-testid="filter-service-all">{copy.allServices}</SelectItem>
              {SERVICES_FOR_FILTER.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`filter-service-${s.key}`}>
                  {lang === "en" ? s.en : s.es}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(countryFilter !== "all" || serviceFilter !== "all" || query) && (
            <button
              type="button"
              data-testid="filter-clear"
              onClick={() => { setCountryFilter("all"); setServiceFilter("all"); setQuery(""); setVisible(PAGE_SIZE); }}
              className="text-xs uppercase tracking-widest text-[#d4af37] hover:underline md:col-span-1"
            >
              {copy.clearFilters}
            </button>
          )}
        </div>

        {/* Comments grid */}
        {shownFiltered.length === 0 ? (
          <div className="text-center py-12 text-neutral-500 italic" data-testid="comments-empty">
            {copy.noResults}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {shownFiltered.map((c, i) => (
              <CommentCard
                key={c.id}
                comment={c}
                index={i}
                extraLikes={extraLikes[c.id] || 0}
                liked={likedSet.has(c.id)}
                onLike={() => handleLike(c.id)}
                lang={lang}
                isAdmin={isAdmin}
                onAdminBumpLikes={adminBumpLikes}
                onAdminDelete={adminDeleteReal}
                onAdminEdit={adminEditReal}
              />
            ))}
          </div>
        )}

        {/* Load more — enabled when there are more items to reveal */}
        {((hasActiveFilter && visible < filtered.length) ||
          (!hasActiveFilter && visible < TOTAL_COMMENTS)) && (
          <div className="text-center mt-10 space-y-3">
            <p className="text-xs uppercase tracking-widest text-neutral-500">
              {copy.showing} {shownFiltered.length} {copy.of}{" "}
              {hasActiveFilter
                ? filtered.length.toLocaleString()
                : `${TOTAL_COMMENTS.toLocaleString()}+`}
            </p>
            <button
              data-testid="comments-load-more"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="btn-outline-gold px-8 py-3.5 rounded-full uppercase text-xs tracking-[0.2em] inline-flex items-center justify-center gap-2"
            >
              {copy.loadMore}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const CommentCard = ({ comment, index, extraLikes = 0, liked = false, onLike, lang = "es", isAdmin = false, onAdminBumpLikes, onAdminDelete, onAdminEdit }) => {
  const c = comment;
  const totalLikes = c.likes + extraLikes;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.text || "");
  const [editRating, setEditRating] = useState(c.rating || 5);
  const isXlong = c.length === "xlong" || (c.text?.length || 0) > 340;
  const displayed = !expanded && isXlong ? c.text.slice(0, 300).trimEnd() + "…" : c.text;
  const canAdminEdit = isAdmin && c.isReal && c.realId;

  const saveEdit = () => {
    if (!editText.trim()) return;
    onAdminEdit?.(c.realId, { text: editText.trim(), rating: editRating });
    setEditing(false);
  };
  const promptCustomBump = () => {
    const v = window.prompt("¿Cuántos me gusta agregar? (número positivo o negativo)", "10");
    if (!v) return;
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n === 0) return;
    onAdminBumpLikes?.(c.realId, n);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: (index % 12) * 0.04 }}
      data-testid={`comment-${c.id}`}
      className="glass-dark rounded-2xl p-5 border border-white/5 hover:border-[#d4af37]/40 transition-colors flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center font-serif text-sm ${c.avatarColor}`}
        >
          {c.initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-white truncate font-medium">{c.name}</span>
            {c.verified && (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
            <span>{c.country.flag}</span>
            <span className="truncate">{c.country.name}</span>
            <span>·</span>
            <span className="truncate">{c.time}</span>
          </div>
        </div>
      </div>

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-2">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`w-3 h-3 ${s <= (c.rating || 5) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`} />
        ))}
      </div>

      {/* Services chips (real service chips per client) */}
      {c.serviceLabels && c.serviceLabels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`comment-services-${c.id}`}>
          {c.serviceLabels.map((lbl, i2) => {
            const cat = (c.serviceCategories || [])[i2] || (c.serviceCategories || [])[0] || "reading";
            const cls =
              cat === "hechiceria" ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
              : cat === "pacto"     ? "border-cyan-500/40 text-cyan-200 bg-cyan-500/10"
                                    : "border-sky-500/40 text-sky-300 bg-sky-500/10";
            return (
              <span
                key={lbl + i2}
                className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}
              >
                {lbl}
              </span>
            );
          })}
          {c.justHired && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-yellow-500/40 text-yellow-300 bg-yellow-500/10">
              {lang === "en" ? "Just contracted" : "Recién contratado"}
            </span>
          )}
        </div>
      )}

      {/* Text */}
      {editing ? (
        <div className="space-y-2">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" onClick={() => setEditRating(s)} className="p-0.5">
                <Star className={`w-4 h-4 ${s <= editRating ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-600"}`} />
              </button>
            ))}
          </div>
          <textarea
            data-testid={`admin-edit-text-${c.realId}`}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={4}
            className="w-full bg-black/50 border border-[#d4af37]/30 rounded p-2 text-sm text-neutral-200"
          />
          <div className="flex gap-2">
            <button
              data-testid={`admin-edit-save-${c.realId}`}
              onClick={saveEdit}
              className="btn-gold text-[10px] px-3 py-1 rounded-full uppercase tracking-widest"
            >Guardar</button>
            <button
              onClick={() => { setEditing(false); setEditText(c.text); setEditRating(c.rating || 5); }}
              className="text-[10px] px-3 py-1 rounded-full border border-white/10 text-neutral-400"
            >Cancelar</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-neutral-300 leading-relaxed flex-1 whitespace-pre-line">
          {displayed}
        </p>
      )}
      {!editing && isXlong && (
        <button
          type="button"
          data-testid={`toggle-${c.id}`}
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[11px] uppercase tracking-widest text-[#d4af37] hover:underline self-start"
        >
          {expanded ? (lang === "en" ? "Show less" : "Ver menos") : (lang === "en" ? "Show more" : "Ver más")}
        </button>
      )}

      {/* Admin-only quick actions (only visible when logged in as admin) */}
      {canAdminEdit && !editing && (
        <div className="mt-3 pt-3 border-t border-dashed border-[#d4af37]/25 flex flex-wrap items-center gap-1.5" data-testid={`admin-comment-actions-${c.realId}`}>
          <span className="text-[9px] uppercase tracking-widest text-[#d4af37]/80 mr-1">Admin</span>
          <button
            data-testid={`admin-comment-edit-${c.realId}`}
            onClick={() => setEditing(true)}
            className="text-[10px] px-2 py-1 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 flex items-center gap-1"
            title="Editar texto o estrellas"
          ><Edit2 className="w-3 h-3" /> Editar</button>
          <button
            data-testid={`admin-comment-like1-${c.realId}`}
            onClick={() => onAdminBumpLikes?.(c.realId, 1)}
            className="text-[10px] px-2 py-1 rounded border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 flex items-center gap-1"
            title="Sumar 1 like (los demás usuarios no lo sabrán)"
          ><Plus className="w-3 h-3" />1</button>
          <button
            data-testid={`admin-comment-like5-${c.realId}`}
            onClick={() => onAdminBumpLikes?.(c.realId, 5)}
            className="text-[10px] px-2 py-1 rounded border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 flex items-center gap-1"
          ><Plus className="w-3 h-3" />5</button>
          <button
            data-testid={`admin-comment-like10-${c.realId}`}
            onClick={() => onAdminBumpLikes?.(c.realId, 10)}
            className="text-[10px] px-2 py-1 rounded border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 flex items-center gap-1"
          ><Plus className="w-3 h-3" />10</button>
          <button
            data-testid={`admin-comment-like-custom-${c.realId}`}
            onClick={promptCustomBump}
            className="text-[10px] px-2 py-1 rounded border border-white/20 text-neutral-300 hover:bg-white/5"
            title="Sumar cantidad personalizada"
          >···</button>
          <button
            data-testid={`admin-comment-delete-${c.realId}`}
            onClick={() => onAdminDelete?.(c.realId)}
            className="ml-auto text-[10px] px-2 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
            title="Eliminar testimonio del sitio"
          ><Trash2 className="w-3 h-3" /> Borrar</button>
        </div>
      )}

      {/* Footer with interactive Like button */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
        <button
          type="button"
          data-testid={`like-${c.id}`}
          onClick={onLike}
          disabled={liked}
          aria-label={lang === "es" ? "Me gusta" : "Like"}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
            liked
              ? "border-rose-500/50 bg-rose-500/15 text-rose-300 cursor-default"
              : "border-white/10 text-neutral-400 hover:border-rose-500/40 hover:text-rose-300 hover:bg-rose-500/5"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${liked ? "fill-rose-400 text-rose-400" : ""}`} />
          <span>{totalLikes}</span>
        </button>
        {c.verified && (
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">
            {lang === "es" ? "Verificado" : "Verified"}
          </span>
        )}
      </div>
    </motion.div>
  );
};
